import * as React from 'react';

import { UitkBadge, UitkBadgeSize } from 'uitk-react-badge';
import { UitkCardAloha, UitkCardAlohaContentSection, UitkCardAlohaLink } from 'uitk-react-cards';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { trackAnalytics } from 'utility';
import { useStores } from 'utility/hooks';

import { ButtonTracked, FlightPrice, Observer } from 'components/shared';
import { FareDetailsToggle } from '.';
import { FlightsJourneyFare } from 'types/shared';
import { BaggageFeesInformationDetailCard } from 'components/search/listings/offer/details/baggage-fees-information-detail-card';

interface FareTypeCardProps {
  fare: FlightsJourneyFare;
  index: number;
  onFareTypeSelect: (index: number) => void;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded: boolean;
}
export const FareTypeCard = ({ fare, index, onFareTypeSelect, isExpanded, setExpanded }: FareTypeCardProps) => {
  const selectFareAccessibilityMessage = fare.chooseFareAction.accessibilityMessage;
  const { analytics } = useStores();
  const { clickEventAnalytics, fareScrollAnalytics, baggageFeesInformation } = fare;
  const selectFare = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onFareTypeSelect(index);
    trackAnalytics(clickEventAnalytics, analytics);
  };

  const intersectionAction = () => {
    trackAnalytics(fareScrollAnalytics, analytics);
  };

  return (
    <UitkCardAloha selected={fare.selected} border={true} className="all-x-padding-six all-y-padding-six" tag="li">
      <UitkCardAlohaContentSection padded={false}>
        <Observer intersectionAction={intersectionAction}>
          <UitkLayoutFlex space="three" direction="column">
            <UitkLayoutFlex direction="row" justifyContent="space-between">
              <UitkLayoutFlexItem>
                <span aria-hidden={true}>
                  <FlightPrice price={fare.formattedMainPrice || fare.formattedPrice} />
                </span>{' '}
              </UitkLayoutFlexItem>
              {fare.recommendation?.text && (
                <UitkLayoutFlexItem>
                  <UitkBadge text={fare.recommendation.text} size={UitkBadgeSize.LARGE} />
                </UitkLayoutFlexItem>
              )}
            </UitkLayoutFlex>
            <UitkLayoutFlexItem>
              <section className="all-x-margin-six all-b-margin-four all-b-padding-four" data-test-id="fare-type">
                <h3 className="is-visually-hidden">{fare.accessibilityMessage}</h3>
                <UitkHeading level={3} size={300}>
                  <span aria-hidden={true}>{fare.name}</span>
                </UitkHeading>
                <UitkText spacing="one" size={300}>
                  <span aria-hidden={true}>{fare.cabinClass}</span>
                </UitkText>
                <FareDetailsToggle isExpanded={isExpanded} setExpanded={setExpanded} fare={fare} />
              </section>
            </UitkLayoutFlexItem>
            {baggageFeesInformation && (
              <Experiment name="PWA_Sidesheet_redesign">
                <ExperimentControl />
                <ExperimentVariant bucket={1}>
                  <UitkCardAlohaContentSection
                    className="all-t-padding-six"
                    border="top"
                    padded={false}
                    data-test-id="baggage-fee-information"
                  >
                    <BaggageFeesInformationDetailCard {...baggageFeesInformation} />
                  </UitkCardAlohaContentSection>
                </ExperimentVariant>
              </Experiment>
            )}
          </UitkLayoutFlex>
        </Observer>
      </UitkCardAlohaContentSection>
      <UitkCardAlohaLink>
        <ButtonTracked onClick={selectFare} data-test-id="fare-type-select">
          {selectFareAccessibilityMessage}
        </ButtonTracked>
      </UitkCardAlohaLink>
    </UitkCardAloha>
  );
};
